/**
 * Two-factor authentication (email verification & two-factor PRD), for the
 * website and the apps: what a refused two-factor sign-in means, what the
 * code step says, and what Settings says of two-factor.
 *
 * Data-only, so React Native can read it. Import it by subpath
 * (`shared/src/config/two-factor.config`) on mobile.
 */

import {
  SOCIAL_PROVIDER_NAMES,
  socialSignInNotice,
} from './social-sign-in.config';
import { API_ERROR_CODES } from '../types/api/errors';
import { getErrorMessage, getFieldErrorMessage } from '../utils/error-messages';

import type { SignInMethodsAccount } from './sign-in-methods.config';
import type { SocialSignInNotice } from './social-sign-in.config';

/**
 * Why finishing a two-factor sign-in was refused:
 * - `code-refused`: that code or recovery code didn't work, so ask again. The
 *   website's pending sign-in answers the same once it has lapsed.
 * - `sign-in-lapsed`: an app's challenge was spent, lapsed or withdrawn, so go
 *   back to the email and password.
 * - `rate-limited`: five tries a minute.
 */
export type TwoFactorChallengeRefusal =
  'code-refused' | 'sign-in-lapsed' | 'rate-limited';

/**
 * Read a refusal from `authService.twoFactorChallenge`: the API's code, and
 * the field its 422 was under. Undefined for anything else, such as a network
 * failure, which the caller words as a plain failure.
 */
export function twoFactorChallengeRefusal(
  error: unknown
): TwoFactorChallengeRefusal | undefined {
  const { code, field } = (error ?? {}) as { code?: unknown; field?: unknown };

  if (code === API_ERROR_CODES.RATE_LIMITED) return 'rate-limited';
  if (field === 'challenge') return 'sign-in-lapsed';
  if (field === 'code' || field === 'recovery_code') return 'code-refused';

  return undefined;
}

/**
 * How a two-factor sign-in is finished: a code from the authenticator app, or
 * one of the recovery codes. Named as the API names the fields they're sent
 * under, and refused under.
 */
export type TwoFactorSignInMethod = 'code' | 'recovery_code';

/** Said for a failure that isn't the code's, such as the network's. */
const COULD_NOT_LOG_IN = 'Couldn’t log you in. Try again.';

/**
 * Said over the email and password once an app's two-factor sign-in has
 * lapsed: its challenge was spent, ran out or was withdrawn. The API words it
 * its own way, and the auth service refuses a challenge past its time in
 * these words, without asking.
 */
export const TWO_FACTOR_SIGN_IN_LAPSED =
  'This sign-in has lapsed. Enter your email and password again.';

/**
 * What the code step does when the sign-in doesn't finish:
 * - `field`: the code or recovery code was refused. Say why under the field,
 *   and ask again.
 * - `notice`: too many tries, or a failure that isn't the code's. Say so over
 *   the field.
 * - `lapsed`: an app's sign-in can't be finished. Go back to the email and
 *   password, with the notice over them.
 */
export type TwoFactorSignInFailure =
  | { show: 'field'; message: string }
  | { show: 'notice' | 'lapsed'; notice: SocialSignInNotice };

/**
 * Read a failed `completeTwoFactorSignIn`, for the method it sent, as the
 * code step shows it. The website's code step and the apps' share it.
 */
export function twoFactorSignInFailure(
  error: unknown,
  method: TwoFactorSignInMethod
): TwoFactorSignInFailure {
  switch (twoFactorChallengeRefusal(error)) {
    case 'rate-limited':
      return { show: 'notice', notice: socialSignInNotice('rate_limited') };
    case 'sign-in-lapsed':
      return {
        show: 'lapsed',
        notice: { variant: 'warning', body: TWO_FACTOR_SIGN_IN_LAPSED },
      };
    case 'code-refused':
      return {
        show: 'field',
        message:
          getFieldErrorMessage(error, method) ??
          getErrorMessage(error, COULD_NOT_LOG_IN),
      };
    default:
      return {
        show: 'notice',
        notice: {
          variant: 'error',
          body: getErrorMessage(error, COULD_NOT_LOG_IN),
        },
      };
  }
}

/** What the code step says, for the method it's asking for. */
export interface TwoFactorSignInWords {
  title: string;
  lede: string;
  /** The field's label. */
  label: string;
  placeholder: string;
  /** Said before the link to the other method. */
  switchLead: string;
  /** The link to the other method. */
  switchLabel: string;
}

/**
 * Said where a recovery code was spent, logging in or confirming it's you: the
 * API replaced it, so the list someone saved has one that won't work.
 */
export const RECOVERY_CODES_CHANGED = {
  title: 'Your recovery codes changed',
  /** The website shows them, from the toast's View codes. */
  web: 'The code you used is replaced. Save the new list from Settings → Account.',
  /** The apps can't show them (D4), so they say where to. */
  app: 'The code you used is replaced. Save the new list on the website, in Settings → Account.',
} as const;

/**
 * Settings → Account's Two-factor row (D5): its title, and what it says under
 * it. The website turns two-factor on and off there. The apps can't (D4), so
 * theirs also says where it's done.
 */
export const TWO_FACTOR_STATUS = {
  title: 'Two-factor authentication',
  on: {
    web: 'Logging in asks for a code from your authenticator app as well as your password.',
    app: 'Logging in asks for a code from your authenticator app as well as your password. Turn it off, or see your recovery codes, on the website.',
  },
  off: {
    web: 'Off. When it’s on, logging in asks for a code from your authenticator app as well as your password.',
    app: 'Off. When it’s on, logging in asks for a code from your authenticator app as well as your password. Turn it on in Settings → Account on the website.',
  },
} as const;

/**
 * What the Two-factor row says under its title, on the website or in the
 * apps. While Google or Apple is connected it can't be turned on (D7), so the
 * row says why, as Sign-in methods says why Connect waits.
 */
export function twoFactorStatus(
  account: Pick<SignInMethodsAccount, 'twoFactor' | 'provider'>,
  reader: 'web' | 'app'
): string {
  if (account.twoFactor) return TWO_FACTOR_STATUS.on[reader];
  if (account.provider) {
    const name = SOCIAL_PROVIDER_NAMES[account.provider];
    return `Disconnect ${name} first: ${name} sign-ins skip the two-factor code.`;
  }
  return TWO_FACTOR_STATUS.off[reader];
}

/** The method the step's link swaps to. */
export const OTHER_TWO_FACTOR_METHOD: Record<
  TwoFactorSignInMethod,
  TwoFactorSignInMethod
> = {
  code: 'recovery_code',
  recovery_code: 'code',
};

export const TWO_FACTOR_SIGN_IN: Record<
  TwoFactorSignInMethod,
  TwoFactorSignInWords
> = {
  code: {
    title: 'Enter your code',
    lede: 'Open your authenticator app and enter the six-digit code for Dō.',
    label: 'Six-digit code',
    placeholder: '123456',
    switchLead: 'Can’t use your app?',
    switchLabel: 'Use a recovery code instead',
  },
  recovery_code: {
    title: 'Enter a recovery code',
    lede: 'One of the codes you saved when you turned on two-factor. Each works once.',
    label: 'Recovery code',
    placeholder: 'xxxxxxxxxx-xxxxxxxxxx',
    switchLead: 'Have your app?',
    switchLabel: 'Use a code from it instead',
  },
};
